import StoreService from '@/services/StoreService';
import { debounce, hasItems, waitFor } from '@/utils';
import { nullish } from '@/utils/yambda';
import { DebouncedFunc } from 'lodash';
import { DateTime, Interval } from 'luxon';
import { action, computed, observable } from 'mobx';
import qs from 'qs';
import { identity, prop, remove } from 'ramda';
import { createContext } from 'react';

import TrainSessionStore from './TrainSessionStore';
import TransportationActions from '@/models/Transportation/TransportationActions';
import TransportationRequestBuilder from '@/models/Transportation/TransportationRequestBuilder';
import { serializeStationSearchResult } from '@/models/Transportation/serializers/TransportationSearchStations';
import { SHomeBenefitBannerStore } from '@/stores/partial/SHomeBenefitBannerStore';
import { EN_SHOME_TARGET_TYPE } from '@@sapporo/constant/enum/sHome/EN_SHOME_TARGET';

import type {
  ITrainStation,
  ITrainStationSuggestion,
} from '@/models/Transportation/serializers/TransportationSearchStations';
import type { ITrainPolicy } from '@/models/Train/interfaces/ITrainPolicy';
import type { ITrainSHomeTitle } from '@/models/Transportation/interfaces/payloads/ITransportationCommon';
import type { ITrainWidgetNotificationPayload } from '@/models/Train/interfaces/ITrainWidgetNotificationPayload';
import type { ITrainSHomeProps, ITrainSHomeQuery } from '@/pages/train';
import type { IStationSuggestionItem } from '@/models/SearchAutoComplete/interfaces/payloads/ISearchAutoCompleteSuggestionPayload';
import type { ITransportationSearchStationsRequestQueryParams } from '@/models/Transportation/interfaces/ITransportationSearchStationsRequestParams';
import type { THangulBasicConsonant } from '@/utils/HangulUtils';
import { combineHangulConsonantVowel } from '@/utils/HangulUtils';
import { trainPolicy } from '@/models/Train/TrainReserveSerializer';
import IntegratedAutocompleteActions from '@/models/IntegratedAutocomplete/IntegratedAutocompleteActions';
import IntegratedAutocompleteRequestBuilder from '@/models/IntegratedAutocomplete/IntegratedAutocompleteRequestBuilder';
import extractAPIData from '@/utils/functions/extractAPIdata';
import SearchAutoCompleteActions from '@/models/SearchAutoComplete/SearchAutoCompleteActions';
import SearchAutoCompleteRequestBuilder from '@/models/SearchAutoComplete/SearchAutoCompleteRequestBuilder';
export type TTrainJourneyType = 'ONEWAY' | 'ROUND';
export type TTrainStationType = 'DEPARTURE' | 'ARRIVAL';
export type TTrainAlertType = 'NOTAVAILABLE' | 'SAMESTATION';
export type TTrainStationFilterType = '전체' | 'KTX' | THangulBasicConsonant;

export interface ITrainStationSearch {
  departure: ITrainStation | ITrainStationSuggestion;
  arrival: ITrainStation | ITrainStationSuggestion;
}

interface ITrainOperation {
  departureDate?: string;
  departureStationName?: string;
  departureStationCode?: string;
  arrivalStationName?: string;
  arrivalStationCode?: string;
}

export default class TrainSHomeStore extends TrainSessionStore {
  @observable journeyType: TTrainJourneyType = 'ONEWAY';
  @observable stationType: TTrainStationType;
  @observable alertType: TTrainAlertType = 'NOTAVAILABLE';
  @observable inputValue = '';
  @observable stations: ITrainStation[] | null;
  @observable markedStations: ITrainStation[] | null;
  @observable histories: ITrainStationSearch[] = [];
  @observable departureStation: ITrainStation | ITrainStationSuggestion | null = {
    name: '서울',
    code: '0001',
    isKTX: true,
    isMark: true,
  };
  @observable arrivalStation: ITrainStation | ITrainStationSuggestion | null;
  @observable stationSuggestion: IStationSuggestionItem[] | null;
  @observable departureDate: DateTime | null;
  @observable returnDate: DateTime | null;
  @observable passengerCountAdult = 1;
  @observable passengerCountChild = 0;

  @observable isLoading = false;
  @observable isOpenAlert = false;
  @observable widgetNotification: ITrainWidgetNotificationPayload | null;
  @observable query: ITrainSHomeQuery | null;

  readonly transportationActions = new TransportationActions(TransportationRequestBuilder);
  readonly integratedAutocompleteActions = new IntegratedAutocompleteActions(IntegratedAutocompleteRequestBuilder);
  readonly searchAutoCompleteActions = new SearchAutoCompleteActions(SearchAutoCompleteRequestBuilder);
  readonly policy: ITrainPolicy = trainPolicy;
  readonly title: ITrainSHomeTitle;
  readonly sHomeBenefitBannerStore: SHomeBenefitBannerStore;
  readonly trainStationHashKey: string = 'train-station';
  readonly trainDateHashKey: string = 'train-date';
  readonly trainCapacityHashKey: string = 'train-capacity';

  private readonly storageKey = 'train-shome-station-histories';
  private readonly stationAutocomplete: DebouncedFunc<() => void>;

  constructor(props: ITrainSHomeProps) {
    super();

    this.stationAutocomplete = debounce(this.fetchSuggestions, 500);
    this.sHomeBenefitBannerStore = new SHomeBenefitBannerStore(EN_SHOME_TARGET_TYPE.RAILROAD, props.benefitBanner);
    this.title = props.title;
    this.widgetNotification = props.widgetNotification;
    this.query = props.query;

    this.retrieveHistories();
  }

  private static async handleOpenModal(hash: string): Promise<void> {
    await waitFor(100);
    location.hash = hash;
  }

  @action.bound
  initSearchData(): void {
    const storedData = this.getStorage();

    if (storedData.departTrain?.departureStationName && storedData.departTrain?.departureStationCode) {
      this.departureStation = {
        code: storedData.departTrain.departureStationCode,
        name: storedData.departTrain.departureStationName,
      };
    }

    if (storedData.departTrain?.arrivalStationName && storedData.departTrain?.arrivalStationCode) {
      if (this.departureStation?.name !== storedData.departTrain.arrivalStationName) {
        this.arrivalStation = {
          code: storedData.departTrain.arrivalStationCode,
          name: storedData.departTrain.arrivalStationName,
        };
      }
    }

    this.departureDate = storedData.departTrain?.departureDate
      ? DateTime.fromFormat(storedData.departTrain.departureDate, 'yyyy-LL-dd')
      : null;

    if (storedData.returnTrain?.departureDate) {
      this.journeyType = 'ROUND';
      this.returnDate = DateTime.fromFormat(storedData.returnTrain.departureDate, 'yyyy-LL-dd');
    } else {
      const availableEndDate = DateTime.local().plus({ days: this.Duration });
      this.returnDate = this.departureDate?.plus({ day: 1 }) || null;
      if (this.returnDate && this.returnDate > availableEndDate) {
        this.returnDate = availableEndDate;
      }
    }

    this.passengerCountAdult = storedData.adults ?? 1;
    this.passengerCountChild = storedData.child ?? 0;
  }

  @computed
  get Duration(): number {
    const regex = /^P([1-9]+)D/g;
    const durationString = regex.exec(this.policy?.duration || 'P31D')?.[1] || '31';
    const duration = isNaN(Number(durationString)) ? 30 : Number(durationString) - 1;

    return duration;
  }

  /**
   * 예약/선택 가능한 시작 날짜
   * 00시 ~ 02시 : 오늘 + 1일부터 예약 가능
   * 02시 ~ 이후 : 오늘 + 2일부터 예약 가능
   */

  /** 2024.11.11 신규 정책 적용
   *  타 서비스의 일반석 판매와 동일하게 당일 예매 가능 (기존 D-2일 구매 제한 → 출발 20분 전까지 구매 가능)
   *  https://yanolja.atlassian.net/wiki/spaces/YATRANS/pages/511607713/2024-11
   */
  @computed
  get AvailableStartDate(): DateTime {
    const now = DateTime.local();
    return now;
  }

  get AvailableStartDateString(): string {
    return this.AvailableStartDate.toFormat('L월 dd일');
  }

  @computed
  get LastOrderHour(): string {
    return this.policy?.lastOrder.time.split(':')?.[0] || '02';
  }

  @computed
  get LastOrderDays(): number {
    return this.policy?.lastOrder.days || 2;
  }

  @computed
  get Capacity(): {
    min: number;
    max: number;
  } {
    return this.policy?.passengerCapacity || { min: 1, max: 9 };
  }

  @computed
  get CapacityString(): string {
    const capacityArray: string[] = [];

    if (this.passengerCountAdult > 0) {
      capacityArray.push(`성인 ${this.passengerCountAdult}명`);
    }

    if (this.passengerCountChild > 0) {
      capacityArray.push(`아동 ${this.passengerCountChild}명`);
    }

    return capacityArray.join(', ');
  }

  @computed
  get IsActive(): boolean {
    const hasStations = !!this.departureStation && !!this.arrivalStation;
    const hasDates = this.IsRoundTrip ? !!this.departureDate && !!this.returnDate : !!this.departureDate;
    const hasPeople = this.passengerCountAdult > 0 || this.passengerCountChild > 0;

    return hasStations && hasDates && hasPeople;
  }

  @computed
  get StationLabel(): string {
    return this.getStationTypeLabel(this.stationType);
  }

  @computed
  get Histories(): ITrainStationSearch[] {
    return this.histories.slice(0, 5);
  }

  set Histories(value: ITrainStationSearch[]) {
    this.histories = value;
    StoreService.getInstance().set(this.storageKey, JSON.stringify(value));
  }

  @computed
  get IsRoundTrip(): boolean {
    return this.journeyType === 'ROUND';
  }

  @action.bound
  setAlertType(type: 'NOTAVAILABLE' | 'SAMESTATION'): void {
    this.alertType = type;
  }

  @action.bound
  async fetchSuggestions(): Promise<void> {
    if (this.inputValue.trim()) {
      // this.searchAutoCompleteActions.trainSuggestions 에서 integrated API 로 변경
      const data = await extractAPIData(
        this.integratedAutocompleteActions.trainStationSuggestions({
          query: {
            query: this.inputValue,
            size: 50,
          },
        }),
      );
      if (data?.body?.items[0] && hasItems(data?.body?.items[0])) {
        this.stationSuggestion = data.body.items[0].map(({ displayLabel, code, isKtx }) => ({
          name: displayLabel,
          code,
          isKtx,
        }));
      } else {
        this.stationSuggestion = [];
      }
    } else {
      this.stationSuggestion = [];
    }
  }

  @action.bound
  async fetchStations(): Promise<void> {
    if (!this.stations) {
      const stationEntity = this.transportationActions.searchStations({ query: { mark: true } });

      const stationPayload = (await stationEntity).map(prop('data')).caseOf({
        left: nullish,
        right: identity,
      });

      const { stations, markedStations } = serializeStationSearchResult(stationPayload ? stationPayload : []);
      this.stations = stations;
      this.markedStations = markedStations;
    }
  }

  @action.bound
  async fetchFilteredStations(searchOption: TTrainStationFilterType): Promise<void> {
    const searchStationQuery: ITransportationSearchStationsRequestQueryParams =
      searchOption === '전체'
        ? { mark: true }
        : searchOption === 'KTX'
        ? { ktx: true }
        : { mark: true, initial: combineHangulConsonantVowel(searchOption, 'ㅏ') };
    const stationEntity = this.transportationActions.searchStations({ query: searchStationQuery });

    const stationPayload = (await stationEntity).map(prop('data')).caseOf({
      left: nullish,
      right: identity,
    });

    const { stations } = serializeStationSearchResult(stationPayload ? stationPayload : []);
    this.stations = stations;
  }

  @action.bound
  setInputValue(value: string): void {
    this.inputValue = value;

    this.stationAutocomplete();
  }

  @action.bound
  onOpenStationModal(type: TTrainStationType): void {
    this.stationType = type;
    void TrainSHomeStore.handleOpenModal(this.trainStationHashKey);
  }

  @action.bound
  onCloseStationModal(): void {
    this.setInputValue('');
    history.back();
  }

  @action.bound
  onOpenCalendarModal(): void {
    void TrainSHomeStore.handleOpenModal(this.trainDateHashKey);
  }

  @action.bound
  onCloseCalendarModal(): void {
    history.back();
  }

  @action.bound
  onOpenCapacityModal(): void {
    void TrainSHomeStore.handleOpenModal(this.trainCapacityHashKey);
  }

  @action.bound
  onCloseCapacityModal(): void {
    history.back();
  }

  @action.bound
  onOpenAlert(): void {
    this.isOpenAlert = true;
  }

  @action.bound
  onCloseAlert(): void {
    this.isOpenAlert = false;
  }

  @action.bound
  onChooseStation(station: ITrainStation | ITrainStationSuggestion): void {
    if (this.stationType === 'DEPARTURE') {
      if (this.arrivalStation?.code === station.code) {
        this.alertType = 'SAMESTATION';
        this.onOpenAlert();

        return;
      }
      this.departureStation = station;
    } else {
      if (this.departureStation?.code === station.code) {
        this.alertType = 'SAMESTATION';
        this.onOpenAlert();

        return;
      }

      this.arrivalStation = station;
    }
  }

  @action.bound
  onChooseHistory(
    departureStation: ITrainStation | ITrainStationSuggestion,
    arrivalStation: ITrainStation | ITrainStationSuggestion,
  ): void {
    this.departureStation = departureStation;
    this.arrivalStation = arrivalStation;
  }

  @action.bound
  onChooseCapacity(adult: number, children: number): void {
    this.passengerCountAdult = adult;
    this.passengerCountChild = children;
    this.saveCurrentSearchConditionToSessionStorage();
  }

  @action.bound
  onClickStationItem(
    departure: ITrainStation | ITrainStationSuggestion,
    arrival?: ITrainStation | ITrainStationSuggestion,
  ): void {
    if (arrival) {
      this.onChooseHistory(departure, arrival);
    } else {
      this.onChooseStation(departure);
    }

    this.onCloseStationModal();
    this.saveCurrentSearchConditionToSessionStorage();
  }

  @action.bound
  onChooseDates(startDate: DateTime, endDate: DateTime | null): void {
    this.departureDate = startDate;

    if (this.journeyType === 'ONEWAY') {
      const limitedDate = DateTime.local().plus({ day: this.Duration });
      const nextDayOfStart = startDate.plus({ day: 1 });

      if (nextDayOfStart > limitedDate) {
        this.returnDate = startDate;
      } else {
        this.returnDate = nextDayOfStart;
      }
    } else {
      this.returnDate = endDate;
    }

    this.saveCurrentSearchConditionToSessionStorage();
  }

  @action.bound
  setJourneyType(type: TTrainJourneyType): void {
    this.journeyType = type;
    this.saveCurrentSearchConditionToSessionStorage();
  }

  @action.bound
  submit(): void {
    if (!this.IsActive) {
      return;
    }

    this.updateHistory({ departure: this.departureStation!, arrival: this.arrivalStation! });

    const queryString = qs.stringify({ d: 'dpt', t: this.IsRoundTrip ? 'round' : 'one' }, { addQueryPrefix: true });

    location.href = `/train/tickets${queryString}`;
  }

  @action.bound
  switchStation(): void {
    [this.departureStation, this.arrivalStation] = [this.arrivalStation, this.departureStation];
    this.saveCurrentSearchConditionToSessionStorage();
  }

  @action
  retrieveHistories(): void {
    if (!process.browser) {
      return;
    }

    const savedHistories: ITrainStationSearch[] | null = StoreService.getInstance().get(this.storageKey);
    if (!savedHistories || !Array.isArray(savedHistories) || !savedHistories.length) {
      return;
    }

    this.histories = savedHistories.filter((entry) => entry.arrival?.code && entry.departure?.code);
  }

  @action.bound
  updateHistory(entry: ITrainStationSearch): void {
    const currentIndex = this.histories.findIndex(
      (history) => history.arrival.code === entry.arrival.code && history.departure.code === entry.departure.code,
    );

    if (currentIndex === -1) {
      return this.prependHistory(entry);
    }

    const newEntries = [entry, ...this.histories.slice(0, currentIndex), ...this.histories.slice(currentIndex + 1)];
    this.saveHistory(newEntries);
  }

  @action.bound
  prependHistory(entry: ITrainStationSearch): void {
    const newEntries = [entry, ...this.histories].slice(0, 5);

    this.saveHistory(newEntries);
  }

  @action.bound
  deleteHistoryAt(index: number): void {
    if (this.histories.length < index) {
      return;
    }

    this.Histories = remove(index, 1, this.histories);
  }

  @action.bound
  deleteAllHistories(): void {
    this.Histories = [];
  }

  /*
   * 서브홈 쿼리 스펙
   * url: https://yanolja.atlassian.net/wiki/spaces/FES/pages/103744301
   * */
  @action.bound
  validateQueryParams(): void {
    if (!this.query) {
      return;
    }

    const { departureStationCode, arrivalStationCode, departureDate, returnDate, adult, child, tripType } = this.query;

    /** 다음에 해당하는 쿼리가 하나라도 들어온다면 검색 조건을 초기화한다. */
    if (departureStationCode || arrivalStationCode || departureDate || returnDate || adult || child || tripType) {
      this.cleanStorage();
      // SRP 에서 SHOME 으로 돌아왔을 시 (history.back()), localStorage 가 아닌 url query param 으로 검색 조건 초기화 방지
      history.replaceState({}, '', '/train');
    }

    /** 어른 + 아이 합쳐서 9명까지 */
    const parsedAdultNum = adult ? +adult : 0;
    const parsedChildrenNum = child ? +child : 0;
    const parsedSum = parsedAdultNum + parsedChildrenNum;
    if (parsedAdultNum >= 0 && 9 >= parsedSum && parsedSum > 0 && parsedChildrenNum >= 0) {
      this.modify({
        adults: parsedAdultNum,
        child: parsedChildrenNum,
      });
    }

    /** journeyType '편도' | '왕복'. this.journeyType 의 default 는 ONEWAY */
    if (tripType === 'oneway') {
      this.journeyType = 'ONEWAY';
    } else if (tripType === 'round') {
      this.journeyType = 'ROUND';
    }

    /** 날짜 유효성 검사 */
    const availableStartDate = this.AvailableStartDate;
    const availableEndDate = DateTime.local().plus({ days: this.Duration });
    const availableInterval = Interval.fromDateTimes(availableStartDate, availableEndDate);

    if (departureDate) {
      const departureDateTime = DateTime.fromFormat(departureDate, 'yyyy-LL-dd');
      if (departureDateTime.isValid && availableInterval.contains(departureDateTime)) {
        this.modify({
          departTrain: {
            departureDate,
          },
        });
      }
    }
    if (returnDate) {
      /** returnDate 쿼리가 있다면 departureDate default 로 availableStartDate 값을 가지게 한다. */
      const departureDateTime = departureDate ? DateTime.fromFormat(departureDate, 'yyyy-LL-dd') : availableStartDate;
      const returnDateTime = DateTime.fromFormat(returnDate, 'yyyy-LL-dd');
      if (
        returnDateTime.isValid &&
        availableInterval.contains(returnDateTime) &&
        returnDateTime >= departureDateTime &&
        tripType === 'round'
      ) {
        this.modify({
          departTrain: {
            departureDate: departureDate ? departureDate : departureDateTime.toFormat('yyyy-LL-dd'),
          },
          returnTrain: {
            departureDate: returnDate,
          },
        });
      }
    }

    /** query 에서 가져온 stationCode 값이 실제 stations 목록에 속해 있는 값인지 확인 */
    const departureStation = this.stations?.find((station) => station.code === departureStationCode);
    if (departureStation) {
      this.modify({
        departTrain: {
          departureStationCode: departureStation.code,
          departureStationName: departureStation.name,
        },
        returnTrain:
          tripType === 'round'
            ? {
                arrivalStationCode: departureStation.code,
                arrivalStationName: departureStation.name,
              }
            : undefined,
      });
    }

    const arrivalStation = this.stations?.find((station) => station.code === arrivalStationCode);
    if (arrivalStation) {
      this.modify({
        departTrain: {
          arrivalStationCode: arrivalStation.code,
          arrivalStationName: arrivalStation.name,
        },
        returnTrain:
          tripType === 'round'
            ? {
                departureStationCode: arrivalStation.code,
                departureStationName: arrivalStation.name,
              }
            : undefined,
      });
    }
  }

  @action.bound
  getTrainOperation(
    departureDate: DateTime | null,
    departureStation: ITrainStation | ITrainStationSuggestion | null,
    arrivalStation: ITrainStation | ITrainStationSuggestion | null,
  ): ITrainOperation {
    return {
      ...(departureDate
        ? {
            departureDate: departureDate.toFormat('yyyy-LL-dd'),
          }
        : {}),
      ...(departureStation
        ? {
            departureStationCode: departureStation.code,
            departureStationName: departureStation.name,
          }
        : {}),
      ...(arrivalStation
        ? {
            arrivalStationCode: arrivalStation.code,
            arrivalStationName: arrivalStation.name,
          }
        : {}),
    };
  }

  @action.bound
  saveCurrentSearchConditionToSessionStorage(): void {
    this.modify({
      adults: this.passengerCountAdult,
      child: this.passengerCountChild,
      departTrain: this.getTrainOperation(this.departureDate, this.departureStation, this.arrivalStation),
      returnTrain: this.IsRoundTrip
        ? this.getTrainOperation(this.returnDate, this.arrivalStation, this.departureStation)
        : undefined,
    });
  }

  getStationTypeLabel(stationType: TTrainStationType): string {
    return stationType === 'DEPARTURE' ? '출발역' : '도착역';
  }

  private saveHistory(histories: readonly ITrainStationSearch[]): void {
    StoreService.getInstance().set(this.storageKey, JSON.stringify(histories));
  }
}

export const TrainSHomeStoreCtx = createContext<TrainSHomeStore>(null!);
